import { FC, useCallback, useContext, useMemo, useRef, useState } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { wafflesKeyColumnName, ColumnConfig, WafflesPaginationProps } from "@app/shared/waffles";
import { Waffles } from "@app/shared/waffles/Waffles";
import { QESButton } from "@app/shared/qes-standards/Button";
import SingleSelectDropdown from "@app/shared/checkbox-dropdown/SingleSelectDropdown";
import { DropdownItem } from "@app/shared/checkbox-dropdown/types";
import { QESTextInput } from "@app/shared/qes-standards/forms/TextInput";
import NetworkSubmissionService from "@app/compliance-connect/network-submission/networkSubmissionService";
import { NetworkSubmissionContext } from "@app/compliance-connect/network-submission/NetworkSubmissionContext";
import ProjectRadioButton from "@app/compliance-connect/network-submission/ProjectRadioButton";
import { NetworkSubmissionWizardAction } from "@app/compliance-connect/network-submission/networkSubmissionWizardActions";
import { createSkeletonRows } from "@app/compliance-connect/network-submission/networkSubmissionHelper";

const ALL_TEMPLATES_DROPDOWN_ITEM: DropdownItem<string> = { key: "All", name: "All" };
const PAGE_SIZE = 10;

interface ProjectTableRow {
  [wafflesKeyColumnName]: string;
  select: React.ReactNode;
  projectName: string;
  gaps: number;
  exceptions: number;
  lineOfBusiness: string;
  template: string;
}

const columnConfig: ColumnConfig = {
  exceptions: {
    displayText: "Exceptions",
    sizeContentToFit: true,
    sortOrder: 4,
    testId: "ns-project-exceptions-col"
  },
  gaps: {
    displayText: "Gaps",
    sizeContentToFit: true,
    sortOrder: 3,
    testId: "ns-project-gaps-col"
  },
  lineOfBusiness: {
    displayText: "Line of Business",
    sizeContentToFit: true,
    sortOrder: 5,
    testId: "ns-project-lob-col"
  },
  projectName: {
    displayText: "Project",
    sortOrder: 2,
    style: { width: "30%" },
    testId: "ns-project-name-col"
  },
  select: {
    displayText: "",
    noSort: true,
    sizeContentToFit: true,
    sortOrder: 1,
    testId: "ns-project-select-col"
  },
  template: {
    displayText: "Template",
    sizeContentToFit: true,
    sortOrder: 6,
    testId: "ns-project-template-col"
  },
};


export const NetworkSubmissionStepSelectProject: FC = () => {
  const wizardContext = useContext(NetworkSubmissionContext);
  const [selectedTemplateItem, setSelectedTemplateItem] = useState<DropdownItem<string>>(ALL_TEMPLATES_DROPDOWN_ITEM);
  const [pendingSearchText, setPendingSearchText] = useState("");
  const [activeSearchText, setActiveSearchText] = useState("");
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);

  const networkSubmissionService = useRef(new NetworkSubmissionService()).current;
  
  const { data: templateData } = useQuery({
    queryFn: ({ signal }) => networkSubmissionService.getProjectTemplates(signal),
    queryKey: ["ns-project-templates"],
  });

  const { data: projectsData, isLoading, isFetching } = useQuery({
    placeholderData: keepPreviousData,
    queryFn: ({ signal }) => networkSubmissionService.getProjects({
      pageIndex,
      pageSize,
      searchText: activeSearchText || undefined,
      template: selectedTemplateItem.key === "All" ? undefined : selectedTemplateItem.key,
    }, signal),
    queryKey: ["ns-projects", pageIndex, pageSize, selectedTemplateItem.key, activeSearchText],
  });

  const templateOptions = useMemo<DropdownItem<string>[]>(() => {
    const templates = templateData ?? [];
    return [ALL_TEMPLATES_DROPDOWN_ITEM, ...templates.map(t => ({ key: t, name: t }))];
  }, [templateData]);

  const pageProjects = projectsData?.networkSubmissions ?? [];
  const totalCount = projectsData?.totalCount ?? 0;

  const handlePageChange = useCallback((pageNumber: number) => {
    setPageIndex(pageNumber - 1);
  }, []);

  const handlePageSizeChange = useCallback((newPageSize: number) => {
    setPageSize(newPageSize);
    setPageIndex(0);
  }, []);

  const handleSearch = () => {
    setActiveSearchText(pendingSearchText);
    setPageIndex(0);
  };

  const handleTemplateChange = (item: DropdownItem<string>) => {
    setSelectedTemplateItem(item);
    setPageIndex(0);
    wizardContext.dispatch({ payload: null, type: NetworkSubmissionWizardAction.SelectProject });
  };

  const handleClear = () => {
    setSelectedTemplateItem(ALL_TEMPLATES_DROPDOWN_ITEM);
    setPendingSearchText("");
    setActiveSearchText("");
    setPageIndex(0);
    wizardContext.dispatch({ payload: null, type: NetworkSubmissionWizardAction.SelectProject });
  };

  const paginationProps: WafflesPaginationProps = useMemo(() => ({
    changePage: handlePageChange,
    enableBackendPagination: true,
    handlePageSizeChange,
    pageSizeOptions: [10, 25, 50, 100],
    recordsPerPage: pageSize,
    showPageSizeSelector: true,
    totalRecords: totalCount,
  }), [pageSize, totalCount, handlePageChange, handlePageSizeChange]);

  const tableData = useMemo<ProjectTableRow[]>(
    () => isLoading
      ? createSkeletonRows<ProjectTableRow>(10, columnConfig) 
      : pageProjects.map((project) => ({
        exceptions: project.exceptions,
        gaps: project.gaps,
        lineOfBusiness: project.lineOfBusiness,
        projectName: project.projectName,
        select: <ProjectRadioButton project={project} />,
        template: project.template,
        [wafflesKeyColumnName]: project.id.toString(),
      })), [isLoading, pageProjects]);

  return (
    <>
      <fieldset className="qes-searchbox bg-white d-flex align-items-end gap-4 mb-4 mt-4 p-4">
        <SingleSelectDropdown<string>
          items={templateOptions}
          label="Template"
          selectedItem={selectedTemplateItem}
          onSelectionChanged={handleTemplateChange}
          testId="ns-project-template-filter"
          hideSearch={true}
          containerClassName="ns-project-selection__template-dropdown d-block w-25"
        />
        <QESTextInput
          containerClassName="ns-project-selection__search-input d-block w-25"
          label="Search"
          onChange={e => setPendingSearchText(e.target.value)}
          onKeyDown={e => { if (e.key === "Enter") handleSearch(); }}
          placeholder="Search..."
          testId="ns-project-search-input"
          value={pendingSearchText}
        />
        <div className="d-flex align-items-end gap-3 ms-4">
          <QESButton
            qesType="outline"
            qesTestId="ns-project-search"
            onClick={handleSearch}
          >
            Search
          </QESButton>
          <QESButton
            qesType="secondary"
            qesTestId="ns-project-clear-link"
            className="btn-link"
            onClick={handleClear}
          >
            Clear All
          </QESButton>
        </div>
      </fieldset>
      <Waffles<ProjectTableRow>
        columns={columnConfig}
        data={tableData}
        enablePagination={true}
        fullWidth={true}
        loading={isLoading || isFetching}
        paginationProps={paginationProps}
        showCheckboxes={false}
        testId="ns-project-selection-table"
      />
    </>
  );
};
